import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ProfilePhotoInput } from '@/components/ui/profile-photo-input';
import { DialogClose, DialogFooter } from '@/components/ui/dialog';
import { useEditProfileForm } from '../useAuthForm';
import { editProfileFormSchema } from '../auth.schema';
import { useUpdateUserDataMutation } from '../auth.api.slice';

interface ProfileFormProps {
  defaultValues?: z.infer<typeof editProfileFormSchema>;
  handleDialog: (open: boolean) => void;
}

function EditProfileForm({ defaultValues, handleDialog }: ProfileFormProps) {
  const form = useEditProfileForm(defaultValues);
  const [updateUser, { isLoading: isUpdateUserLoading }] =
    useUpdateUserDataMutation();

  const handleUpdateUser = async (
    values: z.infer<typeof editProfileFormSchema>
  ) => {
    try {
      await updateUser(values).unwrap();
      toast.success('Profile updated successfully');
      handleDialog(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit(handleUpdateUser)}
      >
        <div className="flex flex-col items-center py-2 gap-4">
          <FormField
            control={form.control}
            name="profilePicture"
            render={({ field }) => (
              <FormItem className="flex flex-col items-center gap-2">
                <FormControl>
                  <ProfilePhotoInput
                    {...field}
                    value={field.value}
                    onChange={field.onChange}
                    className="h-24 w-24"
                  />
                </FormControl>
                <FormLabel>Profile Photo</FormLabel>
              </FormItem>
            )}
          />

          <div className="grid w-full gap-4">
            <FormField
              control={form.control}
              name="firstname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your first name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={isUpdateUserLoading}>
            Save changes
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

export default EditProfileForm;
